'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { tr } from '$/components/cms/helpers';
import { useClientContext } from '$/modules/context/client';

interface ExpandableTextProps {
  className?: string;
  text: string;
  maxChars?: number;
  maxCharsMobile?: number;
  expanded?: boolean;
}

const ExpandableText: React.FC<ExpandableTextProps> = (props) => {
  const {
    text,
    className,
    maxChars = 100,
    maxCharsMobile = 100,
    expanded: propsExpanded,
  } = props;

  const { lang } = useClientContext();
  const [expanded, setExpanded] = useState(propsExpanded);
  const [screenWidth, setScreenWidth] = useState(0);

  // Function to strip HTML tags from text
  const stripHtmlTags = (input: string) => {
    return input.replace(/<\/?[^>]+(>|$)/g, '');
  };

  const updateScreenWidth = () => {
    if (typeof window !== 'undefined') {
      setScreenWidth(window.innerWidth);
    }
  };

  const currentMaxChars = useMemo(() => {
    return screenWidth <= 768 ? maxCharsMobile : maxChars;
  }, [maxChars, maxCharsMobile, screenWidth]);

  const isTruncated = useMemo(() => {
    return stripHtmlTags(text).length > currentMaxChars;
  }, [text, currentMaxChars]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    updateScreenWidth();
    window.addEventListener('resize', updateScreenWidth);
    return () => {
      window.removeEventListener('resize', updateScreenWidth);
    };
  }, []);

  const displayedText = useMemo(() => {
    const cleanText = stripHtmlTags(text);
    let textToDisplay = '';

    if (expanded || !isTruncated) {
      textToDisplay = cleanText;
    } else {
      textToDisplay = cleanText
        ? `${cleanText.substring(0, currentMaxChars)}...`
        : ' ';
    }

    if (isTruncated) {
      textToDisplay += expanded
        ? ` <span class="underline hover:cursor-pointer">${tr('see_less', lang)}</span>`
        : ` <span class="underline hover:cursor-pointer">${tr('see_more', lang)}</span>`;
    }

    return textToDisplay;
  }, [currentMaxChars, expanded, isTruncated, lang, text]);

  return (
    <div className={className}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <p
        className="inline-block"
        onClick={(e) => {
          e.preventDefault();
          setExpanded((prev) => !prev);
        }}
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{ __html: `${displayedText}&nbsp;` }}
      />
    </div>
  );
};

export default ExpandableText;
